import { getCookieToken } from "./utils";

const STORAGE_KEY_PREFIX = "manage360:apiCacheV2:";

function userBucketSuffix() {
  const t = getCookieToken();
  if (!t || t.length < 4) return "anon";
  return String(t).slice(-12);
}

function storageKey() {
  return `${STORAGE_KEY_PREFIX}${userBucketSuffix()}`;
}

/**
 * @typedef {{ data: any, tabId: string }} CacheEntry
 * @returns {{ [requestKey: string]: CacheEntry }}
 */
function readStore() {
  if (typeof window === "undefined" || !window.localStorage) {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(storageKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.entries) {
      return typeof parsed.entries === "object" ? parsed.entries : {};
    }
  } catch (_) {
    /* ignore */
  }
  return {};
}

function writeStore(entries) {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  try {
    const payload = JSON.stringify({ v: 2, entries });
    if (payload.length > 4_000_000) {
      return;
    }
    window.localStorage.setItem(storageKey(), payload);
  } catch (e) {
    console.warn("apiCacheStorage: could not write localStorage", e);
  }
}

/** @param {string} path endpoint without leading slash */
function inferTabFromEndpointPath(path) {
  const s = String(path);
  const qIndex = s.indexOf("?");
  const pathname = qIndex >= 0 ? s.slice(0, qIndex) : s;
  const search = qIndex >= 0 ? s.slice(qIndex + 1) : "";
  const q = new URLSearchParams(search);
  const type = q.get("type");
  const first = pathname.split("/").filter(Boolean)[0] || "";

  if (first === "categories" && type) {
    if (type === "note") return "notes";
    if (type === "file") return "files";
    if (type === "project") return "project";
    if (type === "portfolio") return "portfolio";
    if (type === "url") return "urls";
  }

  if (first === "notes") return "notes";
  if (first === "urls") return "urls";
  if (first === "files" || first === "upload") return "files";
  if (first === "projects") return "project";
  if (first === "portfolios") return "portfolio";
  if (first === "todos") return "todos";
  if (first === "org-tree") return "org-tree";
  if (first === "splitwise") return "splitwise";
  if (first === "calendar-events") return "calendar-events";
  if (first === "daily-chore" || first === "reminder-config") {
    return "daily-chore";
  }
  if (first === "user") return "profile";
  if (first === "dashboard") return "home";
  if (first === "categories") return "project";

  return "other";
}

/**
 * Prefer the active dashboard sub-route for ambiguous `categories?parentId=…` and similar.
 */
function tabFromWindowLocation() {
  if (typeof window === "undefined") return null;
  const p = window.location.pathname.replace(/\/+$/, "");
  if (p === "" || p === "/dashboard") return "home";
  const m = p.match(/\/dashboard\/([^/]+)/);
  return m ? m[1] : null;
}

/**
 * @param {string} endpoint
 * @param {string} method
 */
export function assignCacheTabId(endpoint, method) {
  if (String(method).toUpperCase() !== "GET") return "other";
  const inferred = inferTabFromEndpointPath(endpoint);
  const amb =
    String(endpoint).startsWith("categories?") &&
    !String(endpoint).includes("type=");

  if (amb) {
    const loc = tabFromWindowLocation();
    if (loc && loc !== "home") {
      if (
        ["project", "portfolio", "notes", "files", "urls"].includes(loc)
      ) {
        return loc;
      }
    }
  }
  if (inferred && inferred !== "other") {
    return inferred;
  }
  return tabFromWindowLocation() || inferred;
}

/**
 * @param {string} requestKey
 * @param {any} data
 * @param {string} tabId
 */
export function persistApiCacheEntry(requestKey, data, tabId) {
  const entries = { ...readStore() };
  entries[requestKey] = { data, tabId };
  writeStore(entries);
}

export function readPersistedApiCacheEntry(requestKey) {
  const e = readStore()[requestKey];
  return e && e.data !== undefined ? e : null;
}

/**
 * @returns {Record<string, { data: any, tabId: string }>}
 */
export function readAllPersistedEntries() {
  return { ...readStore() };
}

export function clearAllPersistedApiCache() {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  try {
    window.localStorage.removeItem(storageKey());
  } catch (_) {
    /* ignore */
  }
}

/**
 * @param {string} tabId route segment, e.g. "notes" | "project" | "daily-chore"
 * @returns {string[]} removed request keys
 */
export function clearPersistedApiCacheByTabId(tabId) {
  const entries = readStore();
  const removed = [];
  const next = { ...entries };
  for (const k of Object.keys(next)) {
    if (next[k] && next[k].tabId === tabId) {
      removed.push(k);
      delete next[k];
    }
  }
  writeStore(next);
  return removed;
}

/**
 * @returns {Record<string, any>} requestKey -> data for redux
 */
export function getHydratedApiCacheObject() {
  const entries = readStore();
  const out = {};
  for (const k of Object.keys(entries)) {
    if (entries[k] && "data" in entries[k]) {
      out[k] = entries[k].data;
    }
  }
  return out;
}
